// frontend/src/components/layout/Sidebar.jsx
import React, {
  useState, useEffect,
  useCallback, useMemo, useRef,
}                                    from "react"
import { useSelector, useDispatch }  from "react-redux"
import {
  selectActiveTabId,
  openTab,
}                                    from "../../features/tabsSlice"
import { selectUser }                from "../../features/userSlice"
import {
  loadScreenPrefs,
  saveScreenPrefs,
  selectScreenPrefs,
}                                    from "../../features/uiSlice"
import api                           from "../../api/axiosInstance"
import "./Sidebar.css"

const SCREEN_KEY = "SIDEBAR"

// ── Module order and labels ───────────────────────────────────
const MODULE_ORDER  = ["MAIN", "FI", "SD", "MM", "LE", "IM", "HCM", "SYS"]
const MODULE_LABELS = {
  MAIN: "Main Menu",
  FI:   "Financial Accounting",
  SD:   "Sales & Distribution",
  MM:   "Materials Management",
  LE:   "Logistics Execution",
  IM:   "Inventory & Warehouse",
  HCM:  "HR & Payroll",
  SYS:  "System",
}

// ── Icon resolver ─────────────────────────────────────────────
const getIcon = (k = "", l = "") => {
  k = (k || "").toLowerCase().trim()
  l = (l || "").toLowerCase()
  if (k === "grid"      || l.includes("dashboard"))                            return <IcGrid />
  if (k === "cart"      || l.includes("sales"))                                return <IcCart />
  if (k === "bag"       || l.includes("purchase") || l.includes("procurement")) return <IcBag />
  if (k === "ledger"    || l.includes("account")  || l.includes("general"))     return <IcLedger />
  if (k === "rupee"     || l.includes("finance")  || l.includes("payment")
                        || l.includes("receipt")  || l.includes("journal")
                        || l.includes("voucher"))                              return <IcRupee />
  if (k === "gst"       || l.includes("gst") || l.includes("tax") || l.includes("tds")) return <IcGst />
  if (k === "truck"     || l.includes("logistic") || l.includes("dispatch")
                        || l.includes("freight")  || l.includes("delivery"))    return <IcTruck />
  if (k === "box"       || l.includes("item") || l.includes("material"))        return <IcBox />
  if (k === "warehouse" || l.includes("stock") || l.includes("inventory") || l.includes("warehouse")) return <IcWarehouse />
  if (k === "hr"        || l.includes("employee") || l.includes("payroll") || l.includes("salary"))   return <IcHr />
  if (k === "chart"     || l.includes("report") || l.includes("analysis")
                        || l.includes("trial")  || l.includes("balance"))       return <IcChart />
  if (k === "gear"      || l.includes("setting") || l.includes("admin")
                        || l.includes("system")  || l.includes("config"))       return <IcGear />
  if (k === "users"     || l.includes("customer") || l.includes("supplier")
                        || l.includes("party")    || l.includes("master"))      return <IcUsers />
  return <IcDefault />
}

// ── Build tree from flat Oracle rows (supports arbitrary depth) ─
const buildTree = (rows) => {
  if (!rows?.length) return []
  try {
    const sorted = [...rows].sort((a, b) => {
      const l = (Number(a.MENU_LEVEL) || 0) - (Number(b.MENU_LEVEL) || 0)
      return l !== 0 ? l : (Number(a.MENU_ORDER) || 0) - (Number(b.MENU_ORDER) || 0)
    })

    const childrenOf = {}
    sorted.forEach(r => {
      const pid = r.PARENT_MENU_ID
      if (pid != null && pid !== 0 && pid !== "0" && pid !== "") {
        const key = Number(pid)
        if (!childrenOf[key]) childrenOf[key] = []
        childrenOf[key].push(r)
      }
    })

    const allIds = new Set(sorted.map(r => Number(r.MENU_ID)))
    const roots = sorted.filter(r => {
      const pid = r.PARENT_MENU_ID
      if (pid == null || pid === 0 || pid === "0" || pid === "") return true
      return !allIds.has(Number(pid))
    })

    const toNode = (r) => {
      const kids = (childrenOf[Number(r.MENU_ID)] || []).map(toNode)
      return {
        id:         Number(r.MENU_ID),
        label:      String(r.MENU_NAME   || ""),
        url:        r.MENU_URL           || null,
        icon:       (r.MENU_ICON         || "default").toLowerCase(),
        moduleCode: String(r.MODULE_CODE || ""),
        menuLevel:  Number(r.MENU_LEVEL) || 1,
        menuOrder:  Number(r.MENU_ORDER) || 0,
        formKey:    r.FORM_KEY           || null,
        leaf:       kids.length === 0 && !!r.MENU_URL,
        permissions: {
          canView:   r.CAN_VIEW   === "Y",
          canAdd:    r.CAN_ADD    === "Y",
          canEdit:   r.CAN_EDIT   === "Y",
          canDelete: r.CAN_DELETE === "Y",
          canPrint:  r.CAN_PRINT  === "Y",
          canExport: r.CAN_EXPORT === "Y",
        },
        subs: kids,
      }
    }

    return roots.map(toNode)
  } catch (err) {
    console.error("❌ buildTree error:", err)
    return []
  }
}

// ── Group tree roots by MODULE_CODE ───────────────────────────
const groupByModule = (tree) => {
  const map = {}
  tree.forEach(item => {
    const code = item.moduleCode || "OTHER"
    if (!map[code]) map[code] = []
    map[code].push(item)
  })

  const result  = []
  const handled = new Set()

  MODULE_ORDER.forEach(code => {
    if (map[code]) {
      result.push({ code, label: MODULE_LABELS[code] || code, items: map[code] })
      handled.add(code)
    }
  })
  Object.keys(map).forEach(code => {
    if (!handled.has(code)) {
      result.push({ code, label: code, items: map[code] })
    }
  })
  return result
}

// ── Recursive search — returns matching items + IDs to force-expand ─
const filterTree = (items, q) => {
  if (!q) return { items, expandIds: null }
  const needle    = q.toLowerCase().trim()
  const expandIds = new Set()

  const walk = (nodes) => {
    const out = []
    for (const n of nodes) {
      const selfMatch    = n.label.toLowerCase().includes(needle)
      const childMatches = n.subs?.length ? walk(n.subs) : []
      if (selfMatch || childMatches.length) {
        out.push({ ...n, subs: childMatches })
        if (childMatches.length) expandIds.add(n.id)
      }
    }
    return out
  }
  return { items: walk(items), expandIds }
}

// ── Find which module contains the active tab ─────────────────
const findModuleOfActive = (groups, activeTabId) => {
  if (!activeTabId) return null
  const matches = (node) => {
    const key = node.formKey || String(node.id)
    if (key === activeTabId) return true
    return node.subs?.some(matches)
  }
  for (const grp of groups) {
    if (grp.items.some(matches)) return grp.code
  }
  return null
}

// ── Smooth-animating sub-tree container (measures real height) ─
const SubTree = ({ isOpen, collapsed, openMenus, children }) => {
  const innerRef     = useRef(null)
  const [maxH, setH] = useState(0)

  useEffect(() => {
    if (!innerRef.current) return
    setH(isOpen && !collapsed ? innerRef.current.scrollHeight : 0)
  }, [isOpen, collapsed, openMenus])

  // Re-measure when content size changes (nested items open/close)
  useEffect(() => {
    if (!innerRef.current) return
    const ro = new ResizeObserver(() => {
      if (isOpen && !collapsed && innerRef.current) {
        setH(innerRef.current.scrollHeight)
      }
    })
    ro.observe(innerRef.current)
    return () => ro.disconnect()
  }, [isOpen, collapsed])

  return (
    <div className="sub-tree" style={{ maxHeight: `${maxH}px` }}>
      <div ref={innerRef} className="sub-tree-inner">
        {children}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
const Sidebar = ({ collapsed, onExpand }) => {
  const dispatch     = useDispatch()
  const user         = useSelector(selectUser)
  const activeTabId  = useSelector(selectActiveTabId)
  const sidebarPrefs = useSelector(selectScreenPrefs(SCREEN_KEY)) || {}

  // ── Local state ─────────────────────────────────────────────
  const [rows,         setRows]         = useState([])
  const [status,       setStatus]       = useState("idle")
  const [error,        setError]        = useState(null)
  const [openModules,  setOpenModules]  = useState({})   // multi-expand: { FI: true, SD: true }
  const [openMenus,    setOpenMenus]    = useState({})   // multi-expand: { 102: true, 205: true }
  const [query,        setQuery]        = useState("")

  const fetchedRef     = useRef(false)
  const prefsLoadedRef = useRef(false)

  // ── Fetch menu ──────────────────────────────────────────────
  useEffect(() => {
    if (!user?.emp_id || !user?.token) return
    if (fetchedRef.current) return
    fetchedRef.current = true

    const load = async () => {
      try {
        setStatus("loading"); setError(null)
        console.log("📋 Sidebar: fetching menu for", user.emp_id)
        const t0 = Date.now()

        const { data } = await api.post(
          "/api/menu/getMenuForUser",
          { emp_id: user.emp_id },
          { timeout: 300000 }
        )

        console.log(`✅ Sidebar: ${data.length} rows in ${Date.now() - t0}ms`)
        setRows(data)
        setStatus("done")
      } catch (err) {
        console.error("❌ Sidebar fetch error:", err.message)
        setError(err.message || "Failed to load menu")
        setStatus("error")
        fetchedRef.current = false
      }
    }
    load()
  }, [user?.emp_id, user?.token])  // eslint-disable-line

  // ── Load sidebar prefs from Oracle once ─────────────────────
  useEffect(() => {
    if (!user?.emp_id) return
    dispatch(loadScreenPrefs(SCREEN_KEY))
  }, [user?.emp_id, dispatch])

  // ── Build tree & groups ─────────────────────────────────────
  const tree         = useMemo(() => buildTree(rows),        [rows])
  const moduleGroups = useMemo(() => groupByModule(tree),    [tree])
  const activeModule = useMemo(
    () => findModuleOfActive(moduleGroups, activeTabId),
    [moduleGroups, activeTabId]
  )

  // ── Apply initial openModules from prefs / active / first ──
  useEffect(() => {
    if (prefsLoadedRef.current) return
    if (!moduleGroups.length) return

    const savedMulti  = sidebarPrefs?.openModules
    const savedLegacy = sidebarPrefs?.openModule  // backward compatibility

    if (savedMulti && typeof savedMulti === "object") {
      setOpenModules(savedMulti)
    } else if (savedLegacy && moduleGroups.some(g => g.code === savedLegacy)) {
      setOpenModules({ [savedLegacy]: true })
    } else if (activeModule) {
      setOpenModules({ [activeModule]: true })
    } else {
      setOpenModules({ [moduleGroups[0].code]: true })
    }
    prefsLoadedRef.current = true
  }, [moduleGroups, sidebarPrefs?.openModules, sidebarPrefs?.openModule, activeModule])

  // ── Ensure active module stays open when user switches tabs ─
  useEffect(() => {
    if (!prefsLoadedRef.current) return
    if (!activeModule) return
    setOpenModules(prev => (prev[activeModule] ? prev : { ...prev, [activeModule]: true }))
  }, [activeModule])  // eslint-disable-line

  // ── Persist openModules to Oracle (debounced) ──────────────
  useEffect(() => {
    if (!prefsLoadedRef.current) return
    const t = setTimeout(() => {
      dispatch(saveScreenPrefs({
        screenKey: SCREEN_KEY,
        prefs: { ...sidebarPrefs, openModules },
      }))
    }, 800)
    return () => clearTimeout(t)
  }, [openModules, dispatch])  // eslint-disable-line

  // ── Search filter ──────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!query.trim()) return { groups: moduleGroups, expandIds: null, openAllModules: false }
    const qResult = moduleGroups
      .map(grp => {
        const r = filterTree(grp.items, query)
        return { ...grp, items: r.items, expandIds: r.expandIds }
      })
      .filter(g => g.items.length > 0)

    const mergedExpand = new Set()
    qResult.forEach(g => g.expandIds?.forEach(id => mergedExpand.add(id)))
    return { groups: qResult, expandIds: mergedExpand, openAllModules: true }
  }, [moduleGroups, query])

  // ── Actions ────────────────────────────────────────────────
  const openForm = useCallback((item) => {
    const fk = item.formKey || String(item.id)
    dispatch(openTab({
      id:          fk,
      formType:    fk,
      label:       item.label,
      recordId:    null,
      permissions: item.permissions || {},
    }))
  }, [dispatch])

  const toggleSubMenu = useCallback((id) => {
    if (collapsed) {
      onExpand?.()
      setTimeout(() => setOpenMenus(p => ({ ...p, [id]: true })), 280)
      return
    }
    setOpenMenus(p => ({ ...p, [id]: !p[id] }))
  }, [collapsed, onExpand])

  const toggleModule = useCallback((code) => {
    if (collapsed) {
      onExpand?.()
      setTimeout(() => setOpenModules(prev => ({ ...prev, [code]: true })), 280)
      return
    }
    setOpenModules(prev => ({ ...prev, [code]: !prev[code] }))
  }, [collapsed, onExpand])

  // ── Active checks ──────────────────────────────────────────
  const isActive = useCallback((item) =>
    activeTabId === (item.formKey || String(item.id))
  , [activeTabId])

  const hasActiveChild = useCallback((item) => {
    if (!item.subs?.length) return false
    const check = (nodes) => nodes.some(n => isActive(n) || (n.subs?.length && check(n.subs)))
    return check(item.subs)
  }, [isActive])

  // ── Recursive nav item renderer — supports arbitrary depth ─
  const renderItem = useCallback((item, depth = 0) => {
    const hasSub      = item.subs?.length > 0
    const searchForce = filtered.expandIds && filtered.expandIds.has(item.id)
    const isOpen      = searchForce || !!openMenus[item.id]
    const isCur       = isActive(item)
    const hasAct      = hasActiveChild(item)

    return (
      <React.Fragment key={item.id}>

        <div
          className={[
            "nav-item",
            `depth-${depth}`,
            isCur  ? "current"    : "",
            hasAct ? "has-active" : "",
            isOpen ? "open"       : "",
          ].filter(Boolean).join(" ")}
          data-label={item.label}
          style={!collapsed ? { paddingLeft: `${11 + depth * 14}px` } : undefined}
          onClick={() =>
            !hasSub || item.leaf
              ? openForm(item)
              : toggleSubMenu(item.id)
          }
        >
          <span className="nav-icon">
            {getIcon(item.icon, item.label)}
          </span>
          {!collapsed && (
            <>
              <span className="nav-label">{item.label}</span>
              {hasSub && (
                <span className="nav-arrow"
                  style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
              )}
            </>
          )}
        </div>

        {hasSub && (
          <SubTree isOpen={isOpen} collapsed={collapsed} openMenus={openMenus}>
            {item.subs.map(sub => renderItem(sub, depth + 1))}
          </SubTree>
        )}

      </React.Fragment>
    )
  }, [openMenus, collapsed, isActive, hasActiveChild, openForm, toggleSubMenu, filtered.expandIds])

  // ─────────────────────────────────────────────────────────
  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>

      {/* Search box */}
      {!collapsed && status === "done" && tree.length > 0 && (
        <div className="sb-search">
          <input
            type="text"
            className="sb-search-input"
            placeholder="Search menu…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="sb-search-clear" onClick={() => setQuery("")}>×</button>
          )}
        </div>
      )}

      <div className="sidebar-scroll">

        {status === "loading" && <SidebarLoader />}

        {status === "error" && (
          <div className="sidebar-error">
            <div>{error || "Failed to load menu"}</div>
            <button
              className="sidebar-retry-btn"
              onClick={() => {
                fetchedRef.current = false
                setStatus("idle")
                setRows([])
              }}
            >↺ Retry</button>
          </div>
        )}

        {status === "done" && tree.length === 0 && (
          <div className="sidebar-error">
            No menu items for {user?.emp_id}
          </div>
        )}

        {status === "done" && query && filtered.groups.length === 0 && (
          <div className="sidebar-error">
            No matches for “{query}”
          </div>
        )}

        {/* ── Module accordion (multi-expand) ── */}
        {status === "done" && filtered.groups.map((grp) => {

          // During search: force-open all. Otherwise: respect openModules map.
          const isModuleOpen = filtered.openAllModules || !!openModules[grp.code]

          return (
            <div key={grp.code} className={`sb-module ${isModuleOpen ? "open" : ""}`}>

              <button
                type="button"
                className={[
                  "sb-module-head",
                  isModuleOpen              ? "open"       : "",
                  activeModule === grp.code ? "has-active" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => toggleModule(grp.code)}
              >
                {!collapsed && <span className="sb-module-label">{grp.label}</span>}
                {!collapsed && (
                  <span className="sb-module-arrow"
                    style={{ transform: isModuleOpen ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
                )}
                {collapsed && <span className="sb-module-dot" />}
              </button>

              <SubTree
                isOpen={isModuleOpen}
                collapsed={collapsed}
                openMenus={openMenus}
              >
                {grp.items.flatMap(item => {
                  // If a Level 1 item duplicates the module name,
                  // skip the wrapper and promote its children to the top level.
                  const isDuplicateOfModule =
                    !item.leaf &&
                    item.subs?.length > 0 &&
                    item.label.trim().toLowerCase() === grp.label.trim().toLowerCase()

                  if (isDuplicateOfModule) {
                    return item.subs.map(child => renderItem(child, 0))
                  }
                  return [renderItem(item, 0)]
                })}
              </SubTree>
            </div>
          )
        })}

      </div>

      {/* ── User footer ── */}
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-avatar">
            {(user?.Name || user?.emp_id || "U").charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">
                {user?.Name || user?.emp_id || "User"}
              </div>
              <div className="sidebar-user-role">
                {[user?.entity, user?.division].filter(Boolean).join(" · ") || "SinghalERP"}
              </div>
            </div>
          )}
        </div>
      </div>

    </aside>
  )
}

// ── Skeleton loader ───────────────────────────────────────────
const SidebarLoader = () => (
  <div className="sidebar-loader">
    {[85, 68, 78, 60, 80, 65, 75, 70, 82, 64].map((w, i) => (
      <div key={i} className="skeleton-row" style={{ width: `${w}%` }} />
    ))}
  </div>
)

// ── SVG Icons ─────────────────────────────────────────────────
const I = ({ children }) => (
  <svg
    width="14" height="14"
    viewBox="0 0 16 16"
    fill="none"
    style={{ display: "block", flexShrink: 0 }}
  >
    {children}
  </svg>
)

const IcGrid = () => (
  <I>
    <rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor" opacity=".65"/>
    <rect x="9" y="1" width="6" height="6" rx="1" fill="currentColor" opacity=".65"/>
    <rect x="1" y="9" width="6" height="6" rx="1" fill="currentColor" opacity=".65"/>
    <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" opacity=".65"/>
  </I>
)
const IcCart = () => (
  <I>
    <path d="M1 1h2l2.5 8h7l2-5H5"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="7"  cy="13" r="1.2" fill="currentColor"/>
    <circle cx="12" cy="13" r="1.2" fill="currentColor"/>
  </I>
)
const IcBag = () => (
  <I>
    <rect x="2" y="5" width="12" height="10" rx="1"
          stroke="currentColor" strokeWidth="1.2"/>
    <path d="M5 5V4a3 3 0 016 0v1"
          stroke="currentColor" strokeWidth="1.2"/>
  </I>
)
const IcLedger = () => (
  <I>
    <rect x="2" y="1" width="12" height="14" rx="1"
          stroke="currentColor" strokeWidth="1.2"/>
    <path d="M5 5h6M5 8h6M5 11h4"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinecap="round"/>
  </I>
)
const IcRupee = () => (
  <I>
    <path d="M5 3h6M5 6h6M5 6c3 0 4.5 2 4.5 4s-1.5 4-4.5 4M9 14L5 9"
          stroke="currentColor" strokeWidth="1.3"
          strokeLinecap="round" strokeLinejoin="round"/>
  </I>
)
const IcGst = () => (
  <I>
    <path d="M5 3h6M5 6h6M5 6c3 0 4.5 2 4.5 4s-1.5 4-4.5 4M9 14L5 9"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinecap="round"/>
    <circle cx="13" cy="3" r="2" fill="currentColor" opacity=".6"/>
  </I>
)
const IcTruck = () => (
  <I>
    <rect x="1" y="4" width="10" height="8" rx="1"
          stroke="currentColor" strokeWidth="1.2"/>
    <path d="M11 7h3l1 3v2h-4V7z"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinejoin="round"/>
    <circle cx="4"  cy="13" r="1.5" fill="currentColor"/>
    <circle cx="12" cy="13" r="1.5" fill="currentColor"/>
  </I>
)
const IcBox = () => (
  <I>
    <path d="M1 4.5L8 1l7 3.5-7 3.5L1 4.5z"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinejoin="round"/>
    <path d="M1 4.5V12l7 3 7-3V4.5M8 8v7"
          stroke="currentColor" strokeWidth="1.2"/>
  </I>
)
const IcWarehouse = () => (
  <I>
    <path d="M1 7l7-5 7 5v8H1V7z"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinejoin="round"/>
    <rect x="6" y="9" width="4" height="6"
          stroke="currentColor" strokeWidth="1.2"/>
  </I>
)
const IcHr = () => (
  <I>
    <circle cx="8" cy="5" r="3"
            stroke="currentColor" strokeWidth="1.2"/>
    <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinecap="round"/>
  </I>
)
const IcChart = () => (
  <I>
    <rect x="2"   y="9" width="3" height="5"  fill="currentColor" opacity=".6"/>
    <rect x="6.5" y="6" width="3" height="8"  fill="currentColor" opacity=".6"/>
    <rect x="11"  y="3" width="3" height="11" fill="currentColor" opacity=".6"/>
  </I>
)
const IcGear = () => (
  <I>
    <circle cx="8" cy="8" r="2"
            stroke="currentColor" strokeWidth="1.2"/>
    <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.1 3.1l1.4 1.4M11.5 11.5l1.4 1.4M12.9 3.1l-1.4 1.4M4.5 11.5l-1.4 1.4"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinecap="round"/>
  </I>
)
const IcUsers = () => (
  <I>
    <circle cx="6" cy="5" r="2.5"
            stroke="currentColor" strokeWidth="1.2"/>
    <path d="M1 13c0-2.8 2.2-5 5-5s5 2.2 5 5"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinecap="round"/>
    <path d="M12 7a2 2 0 010 4M13 13c0-1.2-.4-2.3-1-3"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinecap="round"/>
  </I>
)
const IcDefault = () => (
  <I>
    <rect x="2" y="2" width="12" height="12" rx="2"
          stroke="currentColor" strokeWidth="1.2"/>
    <path d="M6 8h4M8 6v4"
          stroke="currentColor" strokeWidth="1.2"
          strokeLinecap="round"/>
  </I>
)

export default Sidebar
